import { inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import {
  API_URL_USER,
  AuthService,
  BaseHttpService,
  EnumPermissions,
  IRole,
  ITokenBaseModel,
  IUser,
  IUserResponse,
  LocalStorageEnum,
  ShowMessageService,
  StorageService,
} from '@olymp/shared';
import { catchError, tap, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { BasePublicHttpService } from './base.public.service';
import { doesUserHasPermission } from '../utils/permission';

export interface IUserChangePassword {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  $basePublicHttpService = inject(BasePublicHttpService);
  $baseHttpService = inject(BaseHttpService);

  $translocoService = inject(TranslocoService);

  $msgService = inject(ShowMessageService);

  $storageService = inject(StorageService);

  $authService = inject(AuthService);

  $router = inject(Router);

  user = signal<IUser | null>(null);

  photoId = signal<string>('');

  get() {
    return this.$baseHttpService.get<IUserResponse>(API_URL_USER.profile).pipe(
      tap((el) => {
        if (el) {
          this.photoId.set(el.result.imageId ?? '');
        }
        el.result.gender = el.result.gender == 2 ? 'girl' : 'man';

        this.user.set(el.result);
      }),
      catchError((err) => {
        this.$storageService.remove(LocalStorageEnum.AccessToken);
        this.$storageService.remove(LocalStorageEnum.RefreshToken);
        this.$router.navigate(['/auth/login']);
        return throwError(() => err);
      }),
    );
  }

  changeUserPhoto(event: Event, userId?: number | null) {
    const input = event.target as HTMLInputElement;

    if (!input.files?.length) {
      return;
    }

    const file = input.files[0];
    const formData = new FormData();
    formData.append('file', file);

    if (userId != null && userId <= 0) {
      return;
    }

    const options =
      userId != null && userId > 0 ? { params: { userId } } : undefined;

    return this.$baseHttpService
      .post<{ result: string }>(API_URL_USER.change_photo, formData, options)
      .pipe(
        tap((el) => {
          if (userId == null) {
            this.user.update((user) => {
              if (user) {
                user.imageId = el.result;
                this.photoId.set(el.result);
              }
              return user;
            });
            this.user.set(this.user());
          }
          this.$msgService.successMsg('successfully_upload');
        }),
      );
  }

  changePassword(value: IUserChangePassword) {
    return this.$baseHttpService
      .post<ITokenBaseModel>(API_URL_USER.change_password, value)
      .pipe(
        tap((el) => {
          this.$authService.setToken(el);
          this.$msgService.successMsg('successfully_changepassword');
        }),
      );
  }

  directorUpdateInstitution(pinfl: string) {
    return this.$baseHttpService.get(
      `director/update-institution-director?pinfl=${pinfl}`,
    );
  }

  changeOlympiadSubType(subType: number) {
    return this.$basePublicHttpService
      .get<{ result: IRole[] }>(`${API_URL_USER.olympiad_sub_type}/${subType}`)
      .pipe(
        tap((res) => {
          if (res?.result) {
            this.user.update((user) => {
              if (user) {
                user.currentOlympiadSubType = subType;
                user.roles = res.result || [];
              }
              return user;
            });
          }
        }),
      );
  }

  refreshUserErp(pinfl: string) {
    return this.$baseHttpService.put(
      `${API_URL_USER.refresh_user_erp}?pinfl=${pinfl}`,
      0,
      {},
    );
  }

  checkPermission(permissions: EnumPermissions[]) {
    return doesUserHasPermission(this.user()?.roles!, permissions);
  }
}
